import { useMemo } from "react";
import type { ImmutableObject } from "@/lib/immutable";
import { storefrontSdk } from "@/storefrontsdk";
import { createStore, type Store, useStore } from "./store";
import { type AppConfigState, useAppConfigState } from "./useAppConfigState";
import type { Product } from "./useInventoryState";
import { type SiteLayoutState, useSiteLayoutState } from "./useSiteLayoutState";

export type CatalogField = {
	Value: string;
	Text: string;
	Selected: boolean;
};

export type WebCatalogWarehouseQtyDto = {
	WarehouseId: string;
	TotalItems: number;
};

export type Catalog = {
	Id: string;
	Name: string;
	UrlName: string;
	DealId?: string;
	DepartmentId: string;
	LocationId: string;
	WarehouseId: string;
	Images: string;
	LandingPageHtml?: string;
	AllowAddingUnavailableItems: boolean;
	GridFields?: CatalogField[];
	ListFields?: CatalogField[];
	DetailFields?: CatalogField[];
	ShoppingCartFields?: CatalogField[];
	HasRental?: boolean;
	HasSales?: boolean;

	gridFieldsByName?: {
		[fieldName: string]: ImmutableObject<CatalogField> | CatalogField;
	};
	listFieldsByName?: {
		[fieldName: string]: ImmutableObject<CatalogField> | CatalogField;
	};
	itemDetailFieldsByName?: {
		[fieldName: string]: ImmutableObject<CatalogField> | CatalogField;
	};
	shoppingCartFieldsByName?: {
		[fieldName: string]: ImmutableObject<CatalogField> | CatalogField;
	};

	WarehousesQtys: WebCatalogWarehouseQtyDto[];
};

export class CatalogModel {
	catalogs?: Catalog[];
	stopLoadingCatalogs: boolean = false;
	isLoadingCatalogs: boolean = false;
	//selectedCatalog?: Catalog;
	expires: Date = new Date();
}

const catalogStateStore = createStore<CatalogModel>(new CatalogModel());

async function waitUntilAsync(condition: () => boolean, time: number = 100) {
	while (!condition()) {
		await new Promise((resolve) => setTimeout(resolve, time));
	}
}

export class CatalogState {
	private catalogState: Store<CatalogModel>;
	private appConfigState: AppConfigState;
	private siteLayoutState: SiteLayoutState;

	constructor(
		catalogState: Store<CatalogModel>,
		appConfigState: AppConfigState,
		siteLayoutState: SiteLayoutState,
	) {
		this.catalogState = catalogState;
		this.appConfigState = appConfigState;
		this.siteLayoutState = siteLayoutState;
	}

	get(): ImmutableObject<CatalogModel> {
		return this.catalogState.getState();
	}

	getExpirationSeconds(): number {
		return 5;
		//return 10 * 60; //10 minutes
	}

	isExpired(): boolean {
		return this.catalogState.getState().expires <= new Date();
	}

	async loadAsync(force: boolean = false) {
		try {
			if (
				this.appConfigState.get().appConfig &&
				(this.isExpired() || force) &&
				!this.catalogState.getState().isLoadingCatalogs &&
				!this.catalogState.getState().stopLoadingCatalogs
			) {
				this.catalogState.setState((prev) => ({
					...prev,
					isLoadingCatalogs: true,
				}));
				const getCatalogsResponse = await storefrontSdk.getCatalogsAsync();
				const catalogs = getCatalogsResponse.data as Catalog[];
				for (let i = 0; i < catalogs.length; i++) {
					const catalog = catalogs[i];
					CatalogState.addFieldIndexesToCatalog(catalog);
				}

				this.catalogState.setState((prev) => ({ ...prev, catalogs }));
				if (getCatalogsResponse.data) {
					this.catalogState.setState((prev) => ({
						...prev,
						//selectedCatalog: getCatalogsResponse.data[0],
						expires: new Date(Date.now() + this.getExpirationSeconds() * 1000),
					}));
				}
			} else if (this.catalogState.getState().isLoadingCatalogs) {
				await waitUntilAsync(
					() => !this.catalogState.getState().isLoadingCatalogs,
				);
			}
		} catch (error) {
			this.catalogState.setState((prev) => ({
				...prev,
				stopLoadingCatalogs: true,
			}));
			this.siteLayoutState.showError(error);
		} finally {
			if (this.catalogState.getState().isLoadingCatalogs) {
				this.catalogState.setState((prev) => ({
					...prev,
					isLoadingCatalogs: false,
				}));
			}
		}
	}

	static async addFieldIndexesToCatalog(catalog: Catalog) {
		// index the dynamic fields so there's a quick way to lookup which fields are enabled
		const gridFieldsByName: {
			[key: string]: ImmutableObject<CatalogField> | CatalogField;
		} = {};
		const listFieldsByName: {
			[key: string]: ImmutableObject<CatalogField> | CatalogField;
		} = {};
		const itemDetailFieldsByName: {
			[key: string]: ImmutableObject<CatalogField> | CatalogField;
		} = {};
		const shoppingCartFieldsByName: {
			[key: string]: ImmutableObject<CatalogField> | CatalogField;
		} = {};
		if (catalog) {
			// index the grid fields
			const gridFields = catalog.GridFields;
			if (gridFields !== undefined) {
				for (let i = 0; i < gridFields.length; i++) {
					const gridField = gridFields[i];
					if (gridField !== undefined) {
						gridFieldsByName[gridField.Value] = gridField;
					}
				}
			}
			catalog.gridFieldsByName = gridFieldsByName;

			// index the list fields
			const listFields = catalog.ListFields;
			if (listFields !== undefined) {
				for (let i = 0; i < listFields.length; i++) {
					const listField = listFields[i];
					if (listField !== undefined) {
						listFieldsByName[listField.Value] = listField;
					}
				}
			}
			catalog.listFieldsByName = listFieldsByName;

			// index the item detail fields
			const itemDetailsFields = catalog.DetailFields;
			if (itemDetailsFields !== undefined) {
				for (let i = 0; i < itemDetailsFields.length; i++) {
					const itemDetailField = itemDetailsFields[i];
					if (itemDetailField !== undefined) {
						itemDetailFieldsByName[itemDetailField.Value] = itemDetailField;
					}
				}
			}
			catalog.itemDetailFieldsByName = itemDetailFieldsByName;

			// index the shopping cart fields
			const shoppingCartFields = catalog.ShoppingCartFields;
			if (shoppingCartFields !== undefined) {
				for (let i = 0; i < shoppingCartFields.length; i++) {
					const shoppingCartField = shoppingCartFields[i];
					if (shoppingCartField !== undefined) {
						shoppingCartFieldsByName[shoppingCartField.Value] =
							shoppingCartField;
					}
				}
			}
			catalog.shoppingCartFieldsByName = shoppingCartFieldsByName;
		}
	}

	getProductCatalog(
		product: ImmutableObject<Product>,
	): ImmutableObject<Catalog> | undefined {
		const catalogs = this.catalogState.getState().catalogs;
		if (catalogs) {
			const catalog = catalogs.find((c) => c.Id === product.WebCatalogId);
			if (catalog) {
				return catalog;
			}
		}
		return undefined;
	}

	getCatalogById(
		catalogid: string | undefined,
	): ImmutableObject<Catalog> | undefined {
		if (catalogid === undefined) return undefined;
		const catalogs = this.catalogState.getState().catalogs;
		if (catalogs) {
			const catalog = catalogs.find((c) => c.Id === catalogid);
			if (catalog) {
				return catalog;
			}
		}
		return undefined;
	}

	getWarehouseCatalogs(warehouseid: string | undefined) {
		let catalogs = this.catalogState.getState().catalogs || [];
		if (warehouseid) {
			catalogs = catalogs.filter((c) => c.WarehouseId === warehouseid);
		}
		return catalogs;
	}
}

export function useCatalogState(): CatalogState {
	const appConfigState = useAppConfigState();
	const siteLayoutState = useSiteLayoutState();
	useStore(catalogStateStore);
	return useMemo(
		() => new CatalogState(catalogStateStore, appConfigState, siteLayoutState),
		[appConfigState, siteLayoutState],
	);
}
